import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import type { EmpFinca, EmpLote, EmpBloque } from '../../types/empacadora';
import {
  ChevronDown, Leaf, CheckCircle, AlertTriangle,
  Save, Upload, Trash2, FileJson, Download, X,
} from 'lucide-react';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface BloqueConteo {
  num: number;
  id_bd?: string;
  area_ha: number;
  area_ef_ha: number;
  plantas: number;
  pl_m2: number;
  geometry?: any;  // geometría original del GeoJSON
  properties?: any;
}

const DEFAULT_FACTOR   = 0.89;
const DEFAULT_DENSIDAD = 6.34;

// ─── Componente ───────────────────────────────────────────────────────────────

interface ConteoCalibradoPageProps {
  initialFincaId?: string;
  initialLoteId?: string;
  onClose?: () => void;
}

export function ConteoCalibradoPage({ initialFincaId, initialLoteId, onClose }: ConteoCalibradoPageProps = {}) {
  const empresaId = useEmpresaId();

  // Catálogo
  const [fincas, setFincas]               = useState<EmpFinca[]>([]);
  const [lotes, setLotes]                 = useState<EmpLote[]>([]);
  const [bloquesBD, setBloquesBD]         = useState<EmpBloque[]>([]);
  const [fincaId, setFincaId]             = useState(initialFincaId ?? '');
  const [loteId, setLoteId]               = useState(initialLoteId ?? '');
  const [loteSeleccionado, setLoteSel]    = useState<EmpLote | null>(null);

  // Factores
  const [factorEf, setFactorEf]     = useState(DEFAULT_FACTOR);
  const [densidadM2, setDensidadM2] = useState(DEFAULT_DENSIDAD);

  // Bloques importados
  const [bloques, setBloques] = useState<BloqueConteo[]>([]);
  const [archivoNombre, setArchivoNombre] = useState('');

  // Calibración manual
  const [conteoCampo, setConteoCampo]   = useState('');
  const [calibradoPor, setCalibradoPor] = useState('');

  // UI
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState<{ tipo: 'ok' | 'err' | 'info'; texto: string } | null>(null);

  const geojsonInputRef = useRef<HTMLInputElement>(null);

  function exportarBloque(b: BloqueConteo) {
    const fc = {
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry: b.geometry || null,
        properties: {
          ID: `B${b.num}`,
          Area_Total_Ha: b.area_ha,
          Area_Efectiva_Ha: b.area_ef_ha,
          Conteo_Estimado: b.plantas,
          pl_m2: b.pl_m2,
          Lote: loteSeleccionado?.nombre || '',
          Finca: fincas.find(f => f.id === fincaId)?.nombre || '',
        },
      }],
    };
    const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `Bloque_${b.num}_${loteSeleccionado?.numero || ''}.geojson`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function exportarTodo() {
    const fc = {
      type: 'FeatureCollection',
      features: bloques.filter(b => b.geometry).map(b => ({
        type: 'Feature',
        geometry: b.geometry,
        properties: {
          ID: `B${b.num}`,
          Area_Total_Ha: b.area_ha,
          Area_Efectiva_Ha: b.area_ef_ha,
          Conteo_Estimado: b.plantas,
          pl_m2: b.pl_m2,
        },
      })),
    };
    const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `Lote_${loteSeleccionado?.numero || ''}_Bloques.geojson`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Catálogo ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!empresaId) return;
    supabase.from('emp_fincas').select('id,nombre,codigo')
      .eq('empresa_id', empresaId).eq('activo', true).order('nombre')
      .then(({ data }) => setFincas((data as EmpFinca[]) || []));
  }, [empresaId]);

  useEffect(() => {
    if (!fincaId) { setLotes([]); setLoteId(''); return; }
    supabase.from('emp_lotes').select('*')
      .eq('finca_id', fincaId).eq('activo', true).order('numero')
      .then(({ data }) => {
        setLotes((data as EmpLote[]) || []);
        if (initialLoteId && !loteId) setLoteId(initialLoteId);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaId]);

  useEffect(() => {
    if (!loteId) { setLoteSel(null); setBloquesBD([]); setBloques([]); setArchivoNombre(''); return; }
    const lote = lotes.find(l => l.id === loteId) || null;
    setLoteSel(lote);
    if (lote) {
      setFactorEf(lote.factor_efectiva ?? DEFAULT_FACTOR);
      setDensidadM2(lote.densidad_m2 ?? DEFAULT_DENSIDAD);
    }
    setBloques([]);
    setArchivoNombre('');
    setMsg(null);
    supabase.from('emp_bloques').select('*')
      .eq('lote_id', loteId).eq('activo', true).order('num')
      .then(({ data }) => setBloquesBD((data as EmpBloque[]) || []));
  }, [loteId, lotes]);

  // ── Importar JSON ───────────────────────────────────────────────────────────

  function importarGeoJSON(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setArchivoNombre(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const fc = JSON.parse(ev.target?.result as string);
        if (fc.type !== 'FeatureCollection') throw new Error('No es un FeatureCollection válido');
        const nuevos: BloqueConteo[] = fc.features.map((feat: any, i: number) => {
          const p = feat.properties || {};
          const area_ha    = Number(p.Area_Total_Ha ?? p.area_ha ?? 0);
          const area_ef_ha = Number(p.Area_Efectiva_Ha ?? p.area_ef_ha ?? (area_ha * factorEf));
          const plantas    = Number(p.Conteo_Estimado ?? p.plantas ?? Math.round(area_ha * 10000 * densidadM2));
          const pl_m2      = area_ha > 0 ? plantas / (area_ha * 10000) : 0;
          const num        = parseInt(String(p.ID || p.id || '').replace(/\D/g, '')) || (i + 1);
          const bd         = bloquesBD.find(b => b.num === num);
          return { num, id_bd: bd?.id, area_ha, area_ef_ha, plantas, pl_m2, geometry: feat.geometry, properties: p };
        });
        nuevos.sort((a, b) => a.num - b.num);
        setBloques(nuevos);
        setMsg({ tipo: 'ok', texto: `${nuevos.length} bloque(s) importados desde "${file.name}". Revise y presione Guardar.` });
      } catch (err: any) {
        setMsg({ tipo: 'err', texto: `Error al leer archivo: ${err.message}` });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  // ── Guardar ─────────────────────────────────────────────────────────────────

  async function guardar() {
    if (!loteId || bloques.length === 0) return;
    setGuardando(true);
    setMsg(null);
    try {
      for (const b of bloques) {
        const payload = {
          empresa_id: empresaId,
          lote_id: loteId,
          num: b.num,
          tipo: 'siembra' as const,
          area_ha: Number(b.area_ha.toFixed(4)),
          plant_count: b.plantas,
          conteo_resultado_payload: {
            area_ha: b.area_ha,
            area_ef_ha: b.area_ef_ha,
            plantas: b.plantas,
            pl_m2: b.pl_m2,
            factor_efectiva: factorEf,
            densidad_m2: densidadM2,
            contado_at: new Date().toISOString(),
          },
          conteo_detectado_at: new Date().toISOString(),
          conteo_modo: 'rgb' as const,
          activo: true,
        };
        if (b.id_bd) {
          await supabase.from('emp_bloques').update(payload).eq('id', b.id_bd);
        } else {
          await supabase.from('emp_bloques').insert(payload);
        }
      }

      // Actualizar lote
      const updateLote: Record<string, any> = {
        area_ha_sembrada: Number(totalEfHa.toFixed(4)),
        factor_efectiva: factorEf,
        densidad_m2: densidadM2,
      };
      if (conteoCampo) {
        const ref = parseInt(conteoCampo);
        if (!isNaN(ref) && ref > 0 && totalHa > 0) {
          const densidadReal = ref / (totalHa * 10000);
          updateLote.calibrado         = true;
          updateLote.conteo_referencia = ref;
          updateLote.fecha_calibracion = new Date().toISOString().slice(0, 10);
          updateLote.calibrado_por     = calibradoPor || null;
          updateLote.densidad_m2       = Number(densidadReal.toFixed(4));
          setDensidadM2(Number(densidadReal.toFixed(4)));
        }
      }
      await supabase.from('emp_lotes').update(updateLote).eq('id', loteId);

      setMsg({ tipo: 'ok', texto: `${bloques.length} bloques guardados — ${totalPlantas.toLocaleString()} plantas registradas en el lote.` });
    } catch (err: any) {
      setMsg({ tipo: 'err', texto: err.message || 'Error al guardar' });
    } finally {
      setGuardando(false);
    }
  }

  // ── Totales ─────────────────────────────────────────────────────────────────

  const totalPlantas = bloques.reduce((s, b) => s + b.plantas, 0);
  const totalHa      = bloques.reduce((s, b) => s + b.area_ha, 0);
  const totalEfHa    = bloques.reduce((s, b) => s + b.area_ef_ha, 0);
  const totalPlM2    = totalHa > 0 ? totalPlantas / (totalHa * 10000) : 0;
  const totalPlHa    = totalHa > 0 ? Math.round(totalPlantas / totalHa) : 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  const containerCls = onClose
    ? 'fixed inset-0 z-[9999] flex flex-col overflow-auto'
    : 'h-full flex flex-col overflow-auto';

  const content = (
    <div className={containerCls} style={{ background: 'var(--surface-base)', color: 'var(--ink)' }}>

      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex-shrink-0 flex items-center justify-between gap-3 flex-wrap"
        style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2">
          {onClose && (
            <button onClick={onClose} className="p-1 rounded mr-1" style={{ color: 'var(--ink-faint)' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--ink)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-faint)')}>
              <X size={16} />
            </button>
          )}
          <Leaf size={18} style={{ color: 'var(--emp-accent-txt)' }} />
          <span className="font-bold text-base">Conteo de Plantas</span>
          {loteSeleccionado?.calibrado && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
              style={{ background: 'var(--tone-success-bg)', color: 'var(--tone-success-text)', border: '1px solid var(--tone-success-border)' }}>
              Calibrado
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <input ref={geojsonInputRef} type="file" accept=".json,.geojson" className="hidden" onChange={importarGeoJSON} />
          <button
            onClick={() => geojsonInputRef.current?.click()}
            disabled={!loteId}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
            style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)', color: 'var(--ink-muted)', opacity: !loteId ? 0.4 : 1 }}
          >
            <Upload size={13} /> Importar JSON
          </button>
          {bloques.length > 0 && (
            <>
              <button
                onClick={exportarTodo}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
                style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}
                title="Exportar todos los bloques como GeoJSON"
              >
                <Download size={13} /> Exportar GeoJSON
              </button>
              <button
                onClick={() => { setBloques([]); setArchivoNombre(''); setMsg(null); }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
                style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)', color: 'var(--tone-warn-text)' }}
              >
                <Trash2 size={13} /> Limpiar
              </button>
              <button
                onClick={guardar}
                disabled={guardando}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium"
                style={{ background: 'var(--emp-accent)', color: 'white', opacity: guardando ? 0.6 : 1 }}
              >
                <Save size={13} /> {guardando ? 'Guardando...' : 'Guardar en BD'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mensaje */}
      {msg && (
        <div className="mx-5 mt-3 flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded text-xs"
          style={{
            background: msg.tipo === 'ok' ? 'var(--tone-success-bg)' : msg.tipo === 'err' ? '#2d1010' : 'var(--tone-info-bg)',
            border: `1px solid ${msg.tipo === 'ok' ? 'var(--tone-success-border)' : msg.tipo === 'err' ? '#7f1d1d' : 'var(--tone-info-border)'}`,
            color: msg.tipo === 'ok' ? 'var(--tone-success-text)' : msg.tipo === 'err' ? '#fca5a5' : 'var(--tone-info-text)',
          }}>
          {msg.tipo === 'ok' ? <CheckCircle size={13} /> : <AlertTriangle size={13} />}
          {msg.texto}
        </div>
      )}

      {/* Selectores Finca / Lote */}
      <div className="px-5 py-3 flex-shrink-0 flex flex-wrap gap-4 items-end"
        style={{ borderBottom: '1px solid var(--line-dim)' }}>
        <div>
          <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--ink-faint)' }}>Finca</label>
          <div className="relative">
            <select value={fincaId} onChange={e => setFincaId(e.target.value)}
              className="appearance-none pr-7 pl-2 py-1.5 rounded text-xs"
              style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)', color: 'var(--ink)', minWidth: 180 }}>
              <option value="">Seleccione...</option>
              {fincas.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--ink-faint)' }} />
          </div>
        </div>
        <div>
          <label className="text-[10px] font-medium block mb-1" style={{ color: 'var(--ink-faint)' }}>Lote</label>
          <div className="relative">
            <select value={loteId} onChange={e => setLoteId(e.target.value)} disabled={!fincaId}
              className="appearance-none pr-7 pl-2 py-1.5 rounded text-xs"
              style={{ background: 'var(--surface-raised)', border: '1px solid var(--line)', color: 'var(--ink)', minWidth: 180, opacity: !fincaId ? 0.5 : 1 }}>
              <option value="">Seleccione...</option>
              {lotes.map(l => <option key={l.id} value={l.id}>Lote {l.numero} — {l.nombre}</option>)}
            </select>
            <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--ink-faint)' }} />
          </div>
        </div>
        {loteSeleccionado && (
          <div className="flex items-center gap-4 text-[11px]" style={{ color: 'var(--ink-faint)' }}>
            <span>Variedad: <b style={{ color: 'var(--ink)' }}>{loteSeleccionado.variedad || 'MD2'}</b></span>
            <span>Factor ef.: <b style={{ color: 'var(--ink)' }}>{factorEf}</b></span>
            <span>Densidad: <b style={{ color: 'var(--ink)' }}>{densidadM2} pl/m²</b></span>
            {loteSeleccionado.calibrado && (
              <span className="flex items-center gap-1" style={{ color: 'var(--tone-success-text)' }}>
                <CheckCircle size={11} /> Calibrado el {loteSeleccionado.fecha_calibracion}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Contenido principal */}
      <div className="flex-1 px-5 py-4 flex flex-col gap-5">

        {/* Estado vacío */}
        {!loteId && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center" style={{ color: 'var(--ink-faint)' }}>
              <Leaf size={36} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium">Seleccione una finca y un lote</p>
            </div>
          </div>
        )}

        {loteId && bloques.length === 0 && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center" style={{ color: 'var(--ink-faint)' }}>
              <FileJson size={40} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium mb-1">Sin datos importados</p>
              <p className="text-xs mb-4">Importe el JSON generado por el script de conteo</p>
              <button
                onClick={() => geojsonInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 rounded text-xs font-medium mx-auto"
                style={{ background: 'var(--emp-accent)', color: 'white' }}
              >
                <Upload size={14} /> Importar JSON
              </button>
            </div>
          </div>
        )}

        {bloques.length > 0 && (
          <>
            {/* Archivo importado */}
            {archivoNombre && (
              <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--ink-faint)' }}>
                <FileJson size={13} />
                <span>Archivo: <b style={{ color: 'var(--ink)' }}>{archivoNombre}</b></span>
              </div>
            )}

            {/* Tarjetas de totales */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Bloques',       val: bloques.length,  fmt: (v: number) => String(v),              accent: false },
                { label: 'Área total',    val: totalHa,         fmt: (v: number) => `${v.toFixed(3)} ha`,   accent: false },
                { label: 'Área efectiva', val: totalEfHa,       fmt: (v: number) => `${v.toFixed(3)} ha`,   accent: false },
                { label: 'pl / m²',       val: totalPlM2,       fmt: (v: number) => v.toFixed(2),           accent: false },
                { label: 'Total plantas', val: totalPlantas,    fmt: (v: number) => v.toLocaleString('es-CR'), accent: true },
                { label: 'Proyección',    val: totalPlHa,       fmt: (v: number) => `${v.toLocaleString('es-CR')} pl/ha`, accent: true },
              ].map(({ label, val, fmt, accent }) => (
                <div key={label} className="rounded-lg p-3"
                  style={{
                    background: accent ? 'var(--emp-accent-bg)' : 'var(--surface-raised)',
                    border: `1px solid ${accent ? 'var(--emp-accent)' : 'var(--line-dim)'}`,
                  }}>
                  <p className="text-[10px] uppercase tracking-wide mb-1"
                    style={{ color: accent ? 'var(--emp-accent-txt)' : 'var(--ink-faint)', opacity: accent ? 0.8 : 1 }}>{label}</p>
                  <p className="text-lg font-bold"
                    style={{ color: accent ? 'var(--emp-accent-txt)' : 'var(--ink)' }}>{fmt(val)}</p>
                </div>
              ))}
            </div>

            {/* Tabla de bloques */}
            <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--line)' }}>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ background: 'var(--surface-raised)' }}>
                    {['Bloque', 'Área (ha)', 'A. Ef. (ha)', 'Plantas', 'pl/m²', 'pl/ha', ''].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left font-semibold"
                        style={{ color: 'var(--ink-muted)', borderBottom: '1px solid var(--line)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bloques.map((b, i) => (
                    <tr key={b.num}
                      style={{ background: i % 2 === 0 ? 'var(--surface-base)' : 'var(--surface-raised)' }}>
                      <td className="px-4 py-2 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>B{b.num}</td>
                      <td className="px-4 py-2" style={{ color: 'var(--ink)' }}>{b.area_ha.toFixed(3)}</td>
                      <td className="px-4 py-2" style={{ color: 'var(--ink)' }}>{b.area_ef_ha.toFixed(3)}</td>
                      <td className="px-4 py-2 font-medium" style={{ color: 'var(--ink)' }}>{b.plantas.toLocaleString()}</td>
                      <td className="px-4 py-2" style={{ color: 'var(--ink-muted)' }}>{b.pl_m2.toFixed(2)}</td>
                      <td className="px-4 py-2" style={{ color: 'var(--ink-muted)' }}>{b.area_ha > 0 ? Math.round(b.plantas / b.area_ha).toLocaleString() : '—'}</td>
                      <td className="px-2 py-2">
                        {b.geometry && (
                          <button onClick={() => exportarBloque(b)} title={`Exportar GeoJSON B${b.num}`}
                            className="p-1 rounded hover:opacity-80 transition-opacity"
                            style={{ color: 'var(--emp-accent-txt)' }}>
                            <Download size={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: 'var(--emp-accent-bg)', borderTop: '2px solid var(--emp-accent)' }}>
                    <td className="px-4 py-2.5 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>TOTAL</td>
                    <td className="px-4 py-2.5 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{totalHa.toFixed(3)}</td>
                    <td className="px-4 py-2.5 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{totalEfHa.toFixed(3)}</td>
                    <td className="px-4 py-2.5 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{totalPlantas.toLocaleString()}</td>
                    <td className="px-4 py-2.5 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{totalPlM2.toFixed(2)}</td>
                    <td className="px-4 py-2.5 font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{totalPlHa.toLocaleString()}</td>
                    <td className="px-2 py-2.5" />
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Panel calibración con conteo físico */}
            <div className="rounded-lg p-4" style={{ background: 'var(--surface-raised)', border: '1px solid var(--line-dim)' }}>
              <p className="text-xs font-semibold mb-3" style={{ color: 'var(--ink-muted)' }}>Calibración con conteo físico en campo (opcional)</p>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-[10px] block mb-1" style={{ color: 'var(--ink-faint)' }}>Plantas contadas en campo</label>
                  <input type="number" placeholder="Ej. 145000" value={conteoCampo}
                    onChange={e => setConteoCampo(e.target.value)}
                    className="px-2 py-1.5 rounded text-xs w-40"
                    style={{ background: 'var(--surface-base)', border: '1px solid var(--line)', color: 'var(--ink)' }} />
                </div>
                <div>
                  <label className="text-[10px] block mb-1" style={{ color: 'var(--ink-faint)' }}>Técnico responsable</label>
                  <input type="text" placeholder="Nombre" value={calibradoPor}
                    onChange={e => setCalibradoPor(e.target.value)}
                    className="px-2 py-1.5 rounded text-xs w-40"
                    style={{ background: 'var(--surface-base)', border: '1px solid var(--line)', color: 'var(--ink)' }} />
                </div>
                {conteoCampo && totalHa > 0 && (
                  <div className="text-xs px-3 py-1.5 rounded"
                    style={{ background: 'var(--tone-info-bg)', border: '1px solid var(--tone-info-border)', color: 'var(--tone-info-text)' }}>
                    Densidad real: <b>{(parseInt(conteoCampo) / (totalHa * 10000)).toFixed(2)} pl/m²</b>
                    &nbsp;(actual: {densidadM2})
                  </div>
                )}
              </div>
              <p className="text-[10px] mt-2" style={{ color: 'var(--ink-faint)' }}>
                Al guardar, el sistema ajustará la densidad del lote con el valor real y lo marcará como calibrado.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );

  if (onClose) return ReactDOM.createPortal(content, document.body);
  return content;
}
